import React, { useEffect, useState } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { useLocation, useNavigate } from "react-router-dom";
import { auth, database as realtimeDb } from "../FirebaseConfig";
import { ref, get } from "firebase/database";
import Lottie from "lottie-react";
import { 
  FaChevronDown, FaHome, FaChevronUp, FaClipboardCheck, 
  FaMapMarkerAlt, FaShippingFast, FaTruck, FaBoxOpen, FaCheckCircle 
} from "react-icons/fa";

const API_URL = "https://us-central1-tghc-web.cloudfunctions.net/";

export default function OrderInfo() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [orderDetails, setOrderDetails] = useState(null);
  const [trackingData, setTrackingData] = useState(null);
  const [error, setError] = useState(null);
  const [trackingId, setTrackingId] = useState(null);
  const [animationData, setAnimationData] = useState(null);
  const [trackingLoading, setTrackingLoading] = useState(true);
  const [showPopup, setShowPopup] = useState(false);
  const [latestScan, setLatestScan] = useState(null);
  const [currentStageIndex, setCurrentStageIndex] = useState(-1);
  const [expandedProducts, setExpandedProducts] = useState(true);

  const navigate = useNavigate();
  const location = useLocation();

  const steps = [
    { label: "Order Placed", icon: <FaClipboardCheck />, status: "CREATED" },
    { label: "Shipped", icon: <FaShippingFast />, status: "PICKED_UP" },
    { label: "In Transit", icon: <FaTruck />, status: "IN_TRANSIT" },
    { label: "Out for Delivery", icon: <FaHome />, status: "OUT_FOR_DELIVERY" },
    { label: "Delivered", icon: <FaBoxOpen />, status: "DELIVERED" },
  ];

  function mapShipmentStatusToStage(status) {
    const statusMap = { "CREATED": 0, "PICKED_UP": 1, "IN_TRANSIT": 2, "OUT_FOR_DELIVERY": 3, "DELIVERED": 4 };
    return statusMap[status] ?? 0;
  }

  useEffect(() => {
    window.scrollTo(0, 0);
    fetch("/assets/General/orderLoad.json")
      .then((res) => res.json())
      .then((data) => setAnimationData(data))
      .catch((err) => console.error("Error loading animation:", err));
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        const orderId = new URLSearchParams(location.search).get("id");
        if (orderId) fetchOrderDetails(orderId);
        else setError("Invalid order ID.");
      } else {
        navigate(`/login?redirect=${location.pathname}${location.search}`);
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  const fetchOrderDetails = async (orderId) => {
    setLoading(true);
    try {
      const snapshot = await get(ref(realtimeDb, `orders/${orderId}`));
      if (snapshot.exists()) {
        const data = snapshot.val();
        setOrderDetails(data);
        setTrackingId(data.trackingId || null);
        setCurrentStageIndex(mapShipmentStatusToStage(data.shipmentStatus));
            // console.log(data.ShipmentData);
      } else {
        setError("Order details not found.");
      }
    } catch (err) {
      setError("Failed to sync with server.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user && trackingId) {
      const fetchTracking = async () => {
        setTrackingLoading(true);
        try {
          const token = await user.getIdToken();
          const response = await fetch(`${API_URL}/trackOrder?waybill=${trackingId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const data = await response.json();
          if (data.ShipmentData?.[0]) {
            const shipment = data.ShipmentData[0].Shipment;
            setTrackingData(shipment);
            setLatestScan(shipment.Scans?.[shipment.Scans.length - 1]);
            
          }
        } catch (e) { console.error("Tracking Error", e); }
        finally { setTrackingLoading(false); }
      };
      fetchTracking();
    }
  }, [user, trackingId]);

  if (loading) return (
    <div className="h-screen flex flex-col justify-center items-center bg-white">
      <div className="w-48 md:w-64"><Lottie animationData={animationData} loop /></div>
      <p className="text-gray-500 font-medium animate-pulse tracking-wide">Securing your order data...</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F8F9FB] mt-30  pb-12 px-4 sm:px-6">
      <div className="max-w-6xl mx-auto">
        
        {/* HEADER SECTION */}
        <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-6 mb-6 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <div className="bg-orange-100 p-3 rounded-2xl text-orange-600">
              <FaBoxOpen size={24} />
            </div>
            <div>
              <h1 className="text-xl md:text-2xl font-black text-gray-900">Order #{new URLSearchParams(location.search).get("id")}</h1>
              <p className="text-gray-500 text-sm font-medium">Placed on {orderDetails?.orderDate || "Recently"}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button 
              onClick={() => setShowPopup(true)}
              className="w-full md:w-auto bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-2xl font-bold transition-all shadow-lg shadow-blue-100 flex items-center justify-center gap-2"
            >
              <FaTruck /> Track History
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          
          {/* MAIN CONTENT COLUMN */}
          <div className="lg:col-span-2 space-y-6">
            
            {/* DELIVERY STEPPER CARD */}
            <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-6 md:p-8">
              <h3 className="text-lg font-bold text-gray-800 mb-10 flex items-center gap-2">
                <FaCheckCircle className="text-green-500" /> Delivery Status
              </h3>
              
              {/* Desktop Horizontal Stepper */}
              <div className="hidden md:flex items-center justify-between relative px-4 mb-4">
                <div className="absolute top-1/2 left-0 w-full h-[2px] bg-gray-100 -translate-y-1/2"></div>
                <div 
                  className="absolute top-1/2 left-0 h-[2px] bg-green-500 -translate-y-1/2 transition-all duration-700"
                  style={{ width: `${(currentStageIndex / (steps.length - 1)) * 100}%` }}
                ></div>
                
                {steps.map((step, index) => (
                  <div key={index} className="relative z-10 flex flex-col items-center">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center transition-all duration-500 border-4 ${
                      index <= currentStageIndex ? "bg-green-500 border-white text-white shadow-xl scale-110" : "bg-white border-gray-50 text-gray-300"
                    }`}>
                      {step.icon}
                    </div>
                    <p className={`mt-4 text-[10px] font-black uppercase tracking-widest text-center ${index <= currentStageIndex ? "text-green-600" : "text-gray-400"}`}>
                      {step.label}
                    </p>
                  </div>
                ))}
              </div>

              {/* Mobile Vertical Stepper */}
              <div className="md:hidden space-y-8 ml-2">
                {steps.map((step, index) => (
                  <div key={index} className="flex items-start gap-4 relative">
                    {index !== steps.length - 1 && (
                      <div className={`absolute left-5 top-10 w-[2px] h-[calc(100%+16px)] ${index < currentStageIndex ? "bg-green-500" : "bg-gray-100"}`}></div>
                    )}
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center z-10 ${
                      index <= currentStageIndex ? "bg-green-500 text-white shadow-lg" : "bg-gray-100 text-gray-400"
                    }`}>
                      {step.icon}
                    </div>
                    <div>
                      <p className={`text-sm font-bold ${index <= currentStageIndex ? "text-gray-900" : "text-gray-400"}`}>{step.label}</p>
                      {index === currentStageIndex && latestScan && (
                        <p className="text-xs text-green-600 font-medium mt-1">{latestScan.ScanDetail.Instructions}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* PRODUCT LIST CARD */}
            <div className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden">
              <button 
                onClick={() => setExpandedProducts(!expandedProducts)}
                className="w-full flex items-center justify-between p-6 hover:bg-gray-50 transition-all"
              >
                <div className="text-left">
                  <h3 className="text-lg font-bold text-gray-800">Your Items</h3>
                  <p className="text-sm text-gray-400">{orderDetails?.products?.length} products in this shipment</p>
                </div>
                <div className="bg-gray-100 p-2 rounded-xl">
                  {expandedProducts ? <FaChevronUp /> : <FaChevronDown />}
                </div>
              </button>
              
              {expandedProducts && (
                <div className="px-6 pb-6 animate-in slide-in-from-top-2 duration-300">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {orderDetails?.products?.map((product, idx) => (
                      <div key={idx} className="flex gap-4 p-4 rounded-2xl border border-gray-50 bg-[#FCFCFD]">
                        <div className="relative">
                          <img src={product.thumbnail} alt={product.name} className="w-20 h-20 object-cover rounded-xl shadow-sm" />
                          <span className="absolute -top-2 -right-2 bg-black text-white text-[10px] px-2 py-1 rounded-lg font-bold">x{product.quantity}</span>
                        </div>
                        <div className="flex-1 flex flex-col justify-center">
                          <p className="text-sm font-bold text-gray-900 line-clamp-1">{product.name}</p>
                          <div className="mt-1 flex items-center gap-2">
                            <span className="text-md font-black text-gray-900">₹{product.actualPrice}</span>
                            {product.price > product.actualPrice && (
                              <span className="text-xs text-gray-400 line-through">₹{product.price}</span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* SIDEBAR COLUMN (Laptop: Right, Mobile: Bottom) */}
          <div className="space-y-6">
            
            {/* SAVINGS BADGE */}
            {(() => {
              const totalOrg = orderDetails?.products?.reduce((sum, p) => sum + (p.price * p.quantity), 0);
              const totalSaved = totalOrg - orderDetails?.totalAmount;
              if (totalSaved <= 0) return null;
              return (
                <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-3xl p-6 text-white shadow-xl shadow-green-100 relative overflow-hidden">
                  <div className="relative z-10">
                    <p className="text-xs font-bold uppercase tracking-widest opacity-80">Order Savings</p>
                    <h4 className="text-3xl font-black mt-1">₹{totalSaved}</h4>
                    <p className="text-sm mt-2 font-medium bg-white/20 inline-block px-3 py-1 rounded-full">Great Deal! 🎉</p>
                  </div>
                  <FaBoxOpen className="absolute -bottom-4 -right-4 text-white/10 text-8xl rotate-12" />
                </div>
              );
            })()}

            {/* PAYMENT SUMMARY */}
            <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-6">
              <h3 className="font-bold text-gray-800 mb-6">Order Summary</h3>
              <div className="space-y-4">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500 font-medium">Subtotal</span>
                  <span className="text-gray-900 font-bold">₹{orderDetails?.totalAmount}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500 font-medium">Shipping</span>
                  <span className="text-green-600 font-bold">FREE</span>
                </div>
                <div className="pt-4 border-t border-gray-100 flex justify-between items-center">
                  <span className="font-black text-gray-900">Total Paid</span>
                  <span className="font-black text-2xl text-blue-600">₹{orderDetails?.totalAmount}</span>
                </div>
              </div>
            </div>

            {/* SHIPPING ADDRESS */}
            <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="bg-red-50 p-2 rounded-lg text-red-500"><FaMapMarkerAlt /></div>
                <h3 className="font-bold text-gray-800">Delivery Address</h3>
              </div>
              <p className="text-sm font-black text-gray-900">{orderDetails?.address?.customerName}</p>
              <p className="text-sm text-gray-500 leading-relaxed mt-2">
                {orderDetails?.address?.addressLine1},<br />
                {orderDetails?.address?.city}, {orderDetails?.address?.State} - <span className="text-gray-900 font-bold">{orderDetails?.address?.Pincode}</span>
              </p>
            </div>
          </div>

        </div>
      </div>

      {/* TRACKING HISTORY MODAL */}
      {showPopup && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/40 backdrop-blur-md" onClick={() => setShowPopup(false)}>
          <div className="bg-white w-full max-w-lg rounded-[2.5rem] shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200" onClick={(e) => e.stopPropagation()}>
            <div className="p-8 border-b flex justify-between items-center bg-gray-50/50">
              <h3 className="text-xl font-black text-gray-900">Tracking Logs</h3>
              <button onClick={() => setShowPopup(false)} className="bg-white shadow-sm p-2 rounded-full hover:bg-gray-100">✕</button>
            </div>
            <div className="p-6 max-h-[50vh] overflow-y-auto space-y-4">
              {trackingData?.Scans?.slice().reverse().map((scan, idx) => (
                <div key={idx} className="flex gap-4 p-4 bg-[#F8F9FB] rounded-2xl border border-gray-100">
                  <div className="mt-1 text-blue-500"><FaMapMarkerAlt /></div>
                  <div>
                    <p className="text-sm font-bold text-gray-900 leading-tight">{scan.ScanDetail.Instructions}</p>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2">
                       <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">{new Date(scan.ScanDetail.ScanDateTime).toLocaleString()}</p>
                       <p className="text-[10px] text-blue-600 font-bold uppercase tracking-widest">{scan.ScanDetail.ScannedLocation}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="p-6">
              <button onClick={() => setShowPopup(false)} className="w-full py-4 bg-gray-900 text-white rounded-2xl font-bold shadow-xl shadow-gray-200">Got it</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}